import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { getDocumentos } from '@/lib/supabaseService';
import type { Documento } from '@/types';

export default function AdminContabilizadorIA() {
  const [allDocs, setAllDocs] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);
  const [vistaActiva, setVistaActiva] = useState<'metricas' | 'rendimiento' | 'precision'>('metricas');

  useEffect(() => {
    (async () => {
      try {
        const docs = await getDocumentos();
        setAllDocs(docs);
      } catch (e) {
        console.error('Error loading docs:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalDocumentos = allDocs.length;
  const procesadosIA = allDocs.filter(d => d.procesadoIA).length;
  const documentosConIA = allDocs.filter(d => d.datosIA);

  const nivelConfianzaPromedio = documentosConIA.length > 0
    ? documentosConIA.reduce((sum, d) => sum + (d.datosIA?.nivelConfianza || 0), 0) / documentosConIA.length
    : 0;

  const documentosAltaConfianza = documentosConIA.filter(d => (d.datosIA?.nivelConfianza || 0) >= 95).length;
  const documentosMediaConfianza = documentosConIA.filter(d => {
    const c = d.datosIA?.nivelConfianza || 0;
    return c >= 80 && c < 95;
  }).length;
  const documentosBajaConfianza = documentosConIA.filter(d => (d.datosIA?.nivelConfianza || 0) < 80).length;

  const validados = allDocs.filter(d => d.estado === 'validado' || d.estado === 'contabilizado').length;
  const tasaValidacion = totalDocumentos > 0 ? (validados / totalDocumentos) * 100 : 0;

  const porTipoDocumento = {
    emitida: documentosConIA.filter(d => d.tipo === 'emitida_pdf' || d.tipo === 'emitida_excel').length,
    recibida: documentosConIA.filter(d => d.tipo === 'recibida_pdf' || d.tipo === 'recibida_excel').length,
    otro: documentosConIA.filter(d => d.tipo === 'otro').length,
  };

  const pct = (n: number) => documentosConIA.length > 0 ? (n / documentosConIA.length) * 100 : 0;

  if (loading) {
    return (
      <Layout rol="admin">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-10 h-10 border-2 border-admin border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout rol="admin">
      <div className="h-screen overflow-y-auto scrollbar-thin">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-6">

          <div className="animate-fade-in">
            <h1 className="text-5xl font-light font-display text-calm-text">Contabilizador IA</h1>
            <p className="text-calm-muted">Supervisión y métricas de rendimiento del motor IA</p>
          </div>

          {/* Top metric cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 animate-slide-up stagger-1">
            <div className="card">
              <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-base text-admin">smart_toy</span>
                <span className="text-xs font-light text-calm-muted uppercase tracking-wider">Procesados IA</span>
              </div>
              <p className="stat-value">{procesadosIA}</p>
              <p className="text-xs text-calm-muted mt-1">de {totalDocumentos} totales</p>
            </div>
            <div className="card">
              <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-base text-success">gps_fixed</span>
                <span className="text-xs font-light text-calm-muted uppercase tracking-wider">Confianza</span>
              </div>
              <p className="stat-value">{nivelConfianzaPromedio.toFixed(1)}%</p>
              <p className="text-xs text-calm-muted mt-1">promedio</p>
            </div>
            <div className="card">
              <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-base text-brand-600">verified_user</span>
                <span className="text-xs font-light text-calm-muted uppercase tracking-wider">Validación</span>
              </div>
              <p className="stat-value">{tasaValidacion.toFixed(0)}%</p>
              <p className="text-xs text-calm-muted mt-1">{validados} validados</p>
            </div>
            <div className="card">
              <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-base text-warning">speed</span>
                <span className="text-xs font-light text-calm-muted uppercase tracking-wider">Alta Confianza</span>
              </div>
              <p className="stat-value">{documentosAltaConfianza}</p>
              <p className="text-xs text-calm-muted mt-1">≥ 95%</p>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-2 animate-slide-up stagger-2">
            {([
              { key: 'metricas', label: 'Métricas', icon: <span className="material-symbols-outlined text-sm">bar_chart</span> },
              { key: 'rendimiento', label: 'Rendimiento', icon: <span className="material-symbols-outlined text-sm">bolt</span> },
              { key: 'precision', label: 'Precisión', icon: <span className="material-symbols-outlined text-sm">gps_fixed</span> },
            ] as const).map(tab => (
              <button
                key={tab.key}
                onClick={() => setVistaActiva(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-huge text-xs font-light transition-all ${
                  vistaActiva === tab.key
                    ? 'bg-admin text-white shadow-sm'
                    : 'bg-surface-raised text-calm-muted hover:text-calm-text'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {vistaActiva === 'metricas' && (
            <div className="space-y-6 animate-slide-up stagger-3">
              <div className="card">
                <h3 className="section-title mb-4">Distribución de Confianza</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: 'Alta (≥95%)', count: documentosAltaConfianza, color: 'emerald' },
                    { label: 'Media (80-94%)', count: documentosMediaConfianza, color: 'amber' },
                    { label: 'Baja (<80%)', count: documentosBajaConfianza, color: 'red' },
                  ].map(b => (
                    <div key={b.label} className={`p-4 rounded-huge bg-${b.color}-50 border border-${b.color}-200`}>
                      <p className={`text-xs font-light text-${b.color}-700 mb-1`}>{b.label}</p>
                      <p className={`text-2xl font-light text-${b.color}-900`}>{b.count}</p>
                      <div className={`mt-2 h-1.5 rounded-full bg-${b.color}-200`}>
                        <div className={`h-1.5 rounded-full bg-${b.color}-500`} style={{ width: `${pct(b.count)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card">
                <h3 className="section-title mb-3">Análisis IA</h3>
                <div className="bg-brand-50 p-4 rounded-huge text-sm text-brand-900 leading-relaxed">
                  Motor IA con confianza promedio del <strong>{nivelConfianzaPromedio.toFixed(1)}%</strong>.{' '}
                  {documentosAltaConfianza > documentosBajaConfianza
                    ? 'La mayoría de documentos se procesan con alta precisión.'
                    : 'Se recomienda revisar los documentos con baja confianza.'}
                  {' '}Tasa de validación: <strong>{tasaValidacion.toFixed(0)}%</strong>.
                </div>
              </div>
            </div>
          )}

          {vistaActiva === 'rendimiento' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-slide-up stagger-3">
              <div className="card">
                <h3 className="section-title mb-4">Velocidad</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Documentos/hora', val: '~120', pct: 80, color: 'bg-admin' },
                    { label: 'Tiempo medio/doc', val: '30s', pct: 90, color: 'bg-success' },
                    { label: 'Disponibilidad', val: '99.8%', pct: 99.8, color: 'bg-success' },
                  ].map(m => (
                    <div key={m.label}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-calm-muted">{m.label}</span>
                        <span className="font-light text-calm-text">{m.val}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-calm-bg">
                        <div className={`h-1.5 rounded-full ${m.color}`} style={{ width: `${m.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card">
                <h3 className="section-title mb-4">Calidad</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Sin errores', val: '87%', cls: 'bg-emerald-50 text-emerald-700' },
                    { label: 'Correcciones manuales', val: '13%', cls: 'bg-amber-50 text-amber-700' },
                    { label: 'Aprendizaje', val: '+2.3%/mes', cls: 'bg-fresh-sky-50 text-fresh-sky-700' },
                  ].map(q => (
                    <div key={q.label} className={`flex items-center justify-between p-3 rounded-huge ${q.cls}`}>
                      <span className="text-sm font-medium">{q.label}</span>
                      <span className="text-sm font-light">{q.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {vistaActiva === 'precision' && (
            <div className="card animate-slide-up stagger-3">
              <h3 className="section-title mb-4">Precisión por Tipo</h3>
              <div className="space-y-3">
                {[
                  { tipo: 'Facturas Emitidas', cantidad: porTipoDocumento.emitida, precision: 96, bg: '#4f46e5' },
                  { tipo: 'Facturas Recibidas', cantidad: porTipoDocumento.recibida, precision: 94, bg: '#059669' },
                  { tipo: 'Otros', cantidad: porTipoDocumento.otro, precision: 82, bg: '#78716c' },
                ].map(item => (
                  <div key={item.tipo} className="p-4 rounded-huge border border-steel-azure-200/40 bg-gradient-to-br from-white to-steel-azure-50/20">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-light text-calm-text text-sm">{item.tipo}</p>
                        <p className="text-xs text-calm-muted">{item.cantidad} procesados</p>
                      </div>
                      <p className="text-xl font-light text-calm-text">{item.precision}%</p>
                    </div>
                    <div className="h-1.5 rounded-full bg-calm-bg">
                      <div className="h-1.5 rounded-full" style={{ width: `${item.precision}%`, backgroundColor: item.bg }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
